/**
 * Critical enterprise feature components for SubscriptionManagement.tsx
 * These are self-contained components imported into the main page.
 *
 * Components:
 *   RenewalCard       — Auto-renew toggle + countdown + reminder selector
 *   UsageAlertsCard   — Per-metric threshold sliders + email + enabled toggle
 *   SpendAnalyticsCard — Monthly spend bar chart + 4 KPIs
 *   AuditLogTab       — Searchable, filterable, paginated billing audit log (Tab 6)
 */

import React, { useState, useEffect } from "react";
import {
  Bell, BellOff, Calendar, ToggleLeft, ToggleRight,
  DollarSign, TrendingUp, FileText, Search, ChevronLeft,
  ChevronRight, Filter, AlertTriangle, CheckCircle2,
  RefreshCw, Loader2, Download, Info, Clock, Shield,
  BarChart2, Users, BarChart3, ArrowUpRight, Zap,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartTooltip, ResponsiveContainer,
} from "recharts";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../shadcn/card";
import { Button } from "../shadcn/button";
import { Badge } from "../shadcn/badge";
import { Progress } from "../shadcn/progress";
import { Skeleton } from "../shadcn/skeleton";
import { Separator } from "../shadcn/separator";

import {
  getUsageAlerts, saveUsageAlerts, getAuditLog, getSpendSummary,
  updateRenewal, type UsageAlerts, type AuditEvent, type SpendSummary,
} from "../../api/subscriptions";
import { downloadCSV } from "../../lib/export";
import { cn } from "../../lib/utils";

// ── Currency formatter ────────────────────────────────────────────────────────
function fmtMoney(amount: number, currency: string): string {
  // amount is in smallest unit (kobo / cents) – divide by 100
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

function fmtMonthLabel(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ── 1. RenewalCard ────────────────────────────────────────────────────────────

interface RenewalCardProps {
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  status: string;
  /** One-time pass (Gumroad): no auto-renewal — access simply expires. */
  oneTime?: boolean;
  /** Current plan tier — enterprise access is managed, not self-serve. */
  plan?: string;
}

export function RenewalCard({ cancelAtPeriodEnd: initialCancel, currentPeriodEnd, status, oneTime = false, plan }: RenewalCardProps) {
  const managed = plan === "enterprise";
  const qc = useQueryClient();
  const [cancelAtEnd, setCancelAtEnd]   = useState(initialCancel);
  const [reminderDays, setReminderDays] = useState<number | null>(14);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () => updateRenewal({ cancel_at_period_end: cancelAtEnd, reminder_days: reminderDays }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      qc.invalidateQueries({ queryKey: ["subscriptions", "overview"] });
    },
  });

  const renewalDate = currentPeriodEnd ? new Date(currentPeriodEnd) : null;
  const daysToRenew = renewalDate
    ? Math.max(0, Math.ceil((renewalDate.getTime() - Date.now()) / 86_400_000))
    : null;

  const urgency =
    daysToRenew === null  ? "none"
    : daysToRenew <= 3    ? "critical"
    : daysToRenew <= 14   ? "warning"
    : "ok";

  return (
    <Card className="overflow-hidden">
      <div
        className={cn("h-0.5 w-full",
          urgency === "critical" ? "bg-red-500" :
          urgency === "warning"  ? "bg-amber-500" : "bg-emerald-500/40"
        )}
      />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            {managed ? "Plan Access" : oneTime ? "Plan Access" : "Renewal Management"}
          </CardTitle>
          {daysToRenew !== null && (
            <span className={cn("text-xs font-semibold px-2 py-0.5 rounded-full",
              urgency === "critical" ? "bg-red-500/15 text-red-500"    :
              urgency === "warning"  ? "bg-amber-500/15 text-amber-500" :
              "bg-green-500/15 text-green-500"
            )}>
              {oneTime ? "Expires" : cancelAtEnd ? "Cancels" : "Renews"} in {daysToRenew}d
            </span>
          )}
        </div>
        {renewalDate && (
          <CardDescription className="text-xs">
            {oneTime ? "Access expires" : cancelAtEnd ? "Subscription ends" : "Next billing date"}:{" "}
            <span className="font-medium text-foreground">{fmtDate(renewalDate.toISOString())}</span>
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Countdown bar */}
        {daysToRenew !== null && (
          <div>
            <div className="flex justify-between text-[11px] text-muted-foreground mb-1.5">
              <span>Current period</span>
              <span>{daysToRenew} days remaining</span>
            </div>
            <Progress
              value={Math.max(5, 100 - Math.min(100, (daysToRenew / 30) * 100))}
              className={cn("h-1.5",
                urgency === "critical" ? "[&>div]:bg-red-500" :
                urgency === "warning"  ? "[&>div]:bg-amber-500" : ""
              )}
            />
          </div>
        )}

        {managed ? (
          /* Enterprise / managed access: no self-serve billing controls. */
          <div className="rounded-xl border border-brand/25 bg-brand/5 px-4 py-3">
            <p className="text-sm font-medium text-text mb-1">Full platform access is active</p>
            <p className="text-xs text-muted">
              Your organization has an active subscription with every capability enabled. Access is
              managed directly with Nucleus Systems — there is nothing to renew or configure here.
            </p>
          </div>
        ) : oneTime ? (
          /* One-time pass: no auto-renew / reminders / cancel — just an expiry note. */
          <div className="rounded-xl border border-border/60 bg-secondary/40 px-4 py-3">
            <p className="text-sm font-medium text-foreground mb-1">One-time pass — nothing to cancel</p>
            <p className="text-xs text-muted-foreground">
              Your plan access was granted for a fixed period and does not auto-renew. When it
              lapses your org drops back to Community (all your repos, findings and baselines
              stay). Renewing extends it: new days stack on top of any time remaining.
            </p>
          </div>
        ) : (
          <>
            {/* Auto-renew toggle */}
            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">Auto-renew</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {cancelAtEnd
                    ? "Subscription will NOT auto-renew — access ends at period end"
                    : "Subscription renews automatically each period"}
                </p>
              </div>
              <button
                onClick={() => setCancelAtEnd((v) => !v)}
                className="flex-shrink-0 ml-4"
                aria-label="Toggle auto-renew"
              >
                {cancelAtEnd
                  ? <ToggleLeft  className="h-8 w-8 text-muted-foreground" />
                  : <ToggleRight className="h-8 w-8 text-green-500" />}
              </button>
            </div>

            {/* Reminder selector */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                <Bell className="h-3 w-3" /> Renewal reminder email
              </p>
              <div className="flex gap-2 flex-wrap">
                {([null, 7, 14, 30] as (number | null)[]).map((d) => (
                  <button
                    key={String(d)}
                    onClick={() => setReminderDays(d)}
                    className={cn(
                      "px-3 py-1.5 rounded-lg text-xs font-medium border transition-all",
                      reminderDays === d
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "border-border/60 text-muted-foreground hover:border-emerald-400 hover:text-foreground"
                    )}
                  >
                    {d === null ? "None" : `${d} days before`}
                  </button>
                ))}
              </div>
            </div>

            <Button
              size="sm"
              className="w-full gap-2"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
            >
              {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {saved ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> Saved</> : "Save Renewal Settings"}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── 2. UsageAlertsCard ───────────────────────────────────────────────────────

const ALERT_LABELS: Record<keyof UsageAlerts, string> = {
  repos:          "Repositories",
  members:        "Team Members",
  scansThisMonth: "Scans / Month",
};

const ALERT_ICONS: Record<keyof UsageAlerts, React.ElementType> = {
  repos:          BarChart3,
  members:        Users,
  scansThisMonth: RefreshCw,
};

export function UsageAlertsCard() {
  const qc = useQueryClient();
  const alertsQuery = useQuery({
    queryKey: ["subscriptions", "alerts"],
    queryFn: getUsageAlerts,
    select: (d) => d.alerts,
  });

  const [draft, setDraft] = useState<UsageAlerts | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (alertsQuery.data && !draft) setDraft(alertsQuery.data);
  }, [alertsQuery.data]);

  const mutation = useMutation({
    mutationFn: () => saveUsageAlerts(draft!),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      qc.invalidateQueries({ queryKey: ["subscriptions", "alerts"] });
    },
  });

  const updateField = (
    metric: keyof UsageAlerts,
    field: string,
    value: unknown
  ) => {
    setDraft((prev: UsageAlerts | null) =>
      prev
        ? { ...prev, [metric]: { ...prev[metric], [field]: value } }
        : prev
    );
  };

  if (alertsQuery.isLoading || !draft) {
    return <Card><CardContent className="p-6"><Skeleton className="h-40 w-full" /></CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          Usage Alert Thresholds
        </CardTitle>
        <CardDescription className="text-xs">
          Receive an email when any metric reaches your configured percentage of its plan limit.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {(Object.keys(ALERT_LABELS) as (keyof UsageAlerts)[]).map((metric) => {
          const cfg  = draft[metric];
          const Icon = ALERT_ICONS[metric];
          return (
            <div key={String(metric)} className="space-y-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{ALERT_LABELS[metric]}</span>
                </div>
                {/* Enabled toggle */}
                <button
                  onClick={() => updateField(metric, "enabled", !cfg.enabled)}
                  className="text-xs font-semibold flex items-center gap-1"
                >
                  {cfg.enabled
                    ? <span className="text-green-500 flex items-center gap-1"><ToggleRight className="h-5 w-5" /> On</span>
                    : <span className="text-muted-foreground flex items-center gap-1"><ToggleLeft className="h-5 w-5" /> Off</span>}
                </button>
              </div>

              {cfg.enabled && (
                <div className="pl-6 space-y-2">
                  {/* Threshold slider */}
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-muted-foreground w-24">Alert at</span>
                    <input
                      type="range"
                      min={50} max={100} step={5}
                      value={cfg.threshold_pct}
                      onChange={(e) => updateField(metric, "threshold_pct", Number(e.target.value))}
                      className="flex-1 accent-emerald-500"
                    />
                    <span className={cn("text-xs font-semibold w-10 text-right",
                      cfg.threshold_pct >= 90 ? "text-red-500"   :
                      cfg.threshold_pct >= 75 ? "text-amber-500" : "text-foreground"
                    )}>
                      {cfg.threshold_pct}%
                    </span>
                  </div>
                  {/* Notify email */}
                  <div className="flex items-center gap-3">
                    <span className="text-[11px] text-muted-foreground w-24">Email</span>
                    <input
                      type="email"
                      placeholder="billing@company.com"
                      value={cfg.notify_email}
                      onChange={(e) => updateField(metric, "notify_email", e.target.value)}
                      className="flex-1 text-xs rounded-lg border border-border/60 bg-secondary/40 px-3 py-1.5 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              )}
              <Separator className="bg-border/40" />
            </div>
          );
        })}

        <Button
          size="sm"
          className="w-full gap-2"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {saved ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> Saved</> : "Save Alert Settings"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── 3. SpendAnalyticsCard ────────────────────────────────────────────────────

export function SpendAnalyticsCard() {
  const spendQuery = useQuery({
    queryKey: ["subscriptions", "spend"],
    queryFn: getSpendSummary,
    staleTime: 60_000,
  });

  if (spendQuery.isLoading) {
    return <Card><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>;
  }

  if (spendQuery.isError || !spendQuery.data) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Info className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No spend data yet — invoices will appear here after your first payment.</p>
        </CardContent>
      </Card>
    );
  }

  const d = spendQuery.data;
  const chartData = d.monthlyBreakdown.map((b) => ({
    month:   fmtMonthLabel(b.month),
    amount:  b.amount_paid / 100,
    invoices: b.invoice_count,
  }));

  const kpis = [
    { label: "YTD Spend",         value: fmtMoney(d.ytd, d.currency),            icon: DollarSign,  color: "text-emerald-500"  },
    { label: "Last Month",        value: fmtMoney(d.lastMonth, d.currency),       icon: Calendar,    color: "text-sky-500"     },
    { label: "Avg / Month",       value: fmtMoney(d.avgMonthly, d.currency),      icon: TrendingUp,  color: "text-amber-500"  },
    { label: "Projected Annual",  value: fmtMoney(d.projectedAnnual, d.currency), icon: BarChart2,   color: "text-green-500"   },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            Spend Analytics
          </CardTitle>
          {d.invoicesFailed > 0 && (
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-500 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {d.invoicesFailed} failed payment{d.invoicesFailed > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <CardDescription className="text-xs">
          Invoice totals for the current fiscal year · {d.invoicesPaid} paid invoice{d.invoicesPaid !== 1 ? "s" : ""}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-4 space-y-6">
        {/* KPI grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kpis.map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="rounded-xl bg-secondary/60 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-muted-foreground leading-snug">{label}</span>
                <Icon className={cn("h-3.5 w-3.5", color)} />
              </div>
              <p className="text-lg font-bold text-foreground">{value}</p>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        {chartData.length > 0 ? (
          <>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monthly Spend (last 12 months)</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/50" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <RechartTooltip
                  formatter={(v: any) => [`$${Number(v).toLocaleString()}`, "Amount"]}
                  contentStyle={{ backgroundColor: "var(--surface1)", border: "1px solid var(--border)", borderRadius: "8px", fontSize: "12px" }}
                />
                <Bar dataKey="amount" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <div className="text-center py-6">
            <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">Spend chart will populate after your first invoice.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── 4. AuditLogTab (Tab 6) ───────────────────────────────────────────────────

const EVENT_COLOR: Record<string, string> = {
  "plan.upgraded":          "text-green-500 bg-green-500/10",
  "plan.downgraded":        "text-amber-500 bg-amber-500/10",
  "billing.payment.failed": "text-red-500 bg-red-500/10",
  "billing.alerts.updated": "text-emerald-500 bg-emerald-500/10",
  "billing.renewal.updated":"text-sky-500 bg-sky-500/10",
  "org.member.removed":     "text-orange-500 bg-orange-500/10",
};

const SOURCE_ICON: Record<string, React.ElementType> = {
  audit_log:      Shield,
  billing_events: DollarSign,
};

function eventColor(eventType: string): string {
  return EVENT_COLOR[eventType] ?? "text-muted-foreground bg-secondary";
}

export function AuditLogTab() {
  const [page,   setPage]   = useState(1);
  const [search, setSearch] = useState("");
  const [type,   setType]   = useState("");
  const [from,   setFrom]   = useState("");
  const [to,     setTo]     = useState("");

  const limit = 20;

  const auditQuery = useQuery({
    queryKey: ["subscriptions", "audit-log", page, search, type, from, to],
    queryFn:  () => getAuditLog({ page, limit, search: search || undefined, type: type || undefined, from: from || undefined, to: to || undefined }),
    staleTime: 30_000,
  });

  const handleExport = () => {
    if (!auditQuery.data?.events) return;
    const rows = auditQuery.data.events.map((e) => ({
      event_type:  e.event_type,
      action:      e.event_action,
      actor:       e.actor_type,
      resource:    e.resource_type,
      source:      e.source,
      date:        e.created_at,
    }));
    downloadCSV(rows, `paxley-audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const data = auditQuery.data;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[160px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search events…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-border/60 bg-secondary/40 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>

            {/* Type filter */}
            <select
              value={type}
              onChange={(e) => { setType(e.target.value); setPage(1); }}
              className="text-sm rounded-lg border border-border/60 bg-secondary/40 text-foreground px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              <option value="">All event types</option>
              {(data?.eventTypes ?? []).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Date range */}
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }}
              className="text-sm rounded-lg border border-border/60 bg-secondary/40 text-foreground px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }}
              className="text-sm rounded-lg border border-border/60 bg-secondary/40 text-foreground px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />

            <Button variant="outline" size="sm" className="gap-1.5 h-9 text-xs" onClick={handleExport} disabled={!data?.events.length}>
              <Download className="h-3.5 w-3.5" /> Export
            </Button>
          </div>

          {data && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {data.total.toLocaleString()} total events · page {data.page} of {Math.max(1, Math.ceil(data.total / limit))}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Event list */}
      <Card>
        <CardContent className="p-0">
          {auditQuery.isLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
            </div>
          ) : !data?.events.length ? (
            <div className="p-12 text-center">
              <Shield className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm font-semibold text-foreground">No audit events found</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {data.events.map((event) => {
                const SrcIcon = SOURCE_ICON[event.source] ?? Shield;
                return (
                  <div key={event.id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-secondary/30 transition-colors">
                    {/* Source icon */}
                    <div className="mt-0.5 flex-shrink-0 p-1.5 rounded-md bg-secondary">
                      <SrcIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={cn("text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide", eventColor(event.event_type))}>
                          {event.event_type.replace(/\./g, " · ")}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {event.actor_type} · {event.resource_type}
                        </span>
                      </div>
                      {event.details && Object.keys(event.details).length > 0 && (
                        <p className="text-[11px] text-muted-foreground/70 mt-0.5 truncate">
                          {JSON.stringify(event.details).slice(0, 120)}
                        </p>
                      )}
                    </div>

                    <time className="text-[11px] text-muted-foreground flex-shrink-0 whitespace-nowrap">
                      {fmtDateTime(event.created_at)}
                    </time>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > limit && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="gap-1.5">
            <ChevronLeft className="h-3.5 w-3.5" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">Page {page} / {Math.ceil(data.total / limit)}</span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!data.hasMore} className="gap-1.5">
            Next <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
